import { useState } from "react";
import {
  useCourses,
  useRoleLearningDefaults,
  useSaveRoleLearningDefaults,
} from "../../backend/queries/learning";
import { CourseAssignmentChoices } from "../../components/learning/CourseAssignmentChoices";
import {
  PageSection,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/patterns";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

type LearningRole = "Admin" | "Agent" | "Client";

export function RoleOnboardingSettings() {
  const [role, setRole] = useState<LearningRole>("Agent");
  const [changes, setChanges] = useState<Record<string, boolean | null>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const courses = useCourses();
  const defaults = useRoleLearningDefaults();
  const save = useSaveRoleLearningDefaults();
  const values: Record<string, boolean> = Object.fromEntries(
    (defaults.data || [])
      .filter((item) => item.role === role)
      .map((item) => [item.course_id, item.required]),
  );
  Object.entries(changes).forEach(([id, value]) => {
    if (value === null) delete values[id];
    else values[id] = value;
  });
  const submit = async () => {
    setError("");
    setMessage("");
    try {
      await save.mutateAsync({
        role,
        courses: Object.entries(values).map(([course_id, required]) => ({
          course_id,
          required,
        })),
      });
      setChanges({});
      setMessage(`Onboarding saved for new ${role.toLowerCase()} accounts.`);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not save onboarding.",
      );
    }
  };
  return (
    <PageSection
      title="Onboarding by role"
      description="Defaults for new accounts. Required courses must be completed before using the portal."
    >
      <div className="grid gap-4">
        <Select
          value={role}
          disabled={save.isPending}
          onValueChange={(value) => {
            setRole(value as LearningRole);
            setChanges({});
            setMessage("");
            setError("");
          }}
        >
          <SelectTrigger
            className="w-full sm:w-56"
            aria-label="Onboarding role"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["Admin", "Agent"].map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {courses.isPending || defaults.isPending ? (
          <LoadingState label="Loading role defaults" />
        ) : courses.error || defaults.error ? (
          <ErrorState
            onRetry={() => {
              courses.refetch();
              defaults.refetch();
            }}
          />
        ) : !courses.data?.some((course) => course.status === "published") ? (
          <EmptyState
            title="Publish a course first"
            description="Published courses will appear here for role-based onboarding."
          />
        ) : (
          <>
            <CourseAssignmentChoices
              courses={courses.data.filter(
                (course) => course.status === "published",
              )}
              values={values}
              defaultRequired
              onChange={(id, value) =>
                setChanges((current) => ({ ...current, [id]: value }))
              }
              disabled={save.isPending}
            />
            <Button
              className="w-fit"
              disabled={save.isPending || !Object.keys(changes).length}
              onClick={submit}
            >
              {save.isPending ? "Saving…" : "Save role defaults"}
            </Button>
          </>
        )}
        {message && (
          <p role="status" className="text-sm">
            {message}
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </PageSection>
  );
}
